import type {
  Float8_Comparison_Exp,
  Bigint_Comparison_Exp,
} from 'graphql/research/types-and-hooks';
import React, { useCallback, useMemo, useReducer } from 'react';
import { useToast, Paragraph } from '@leafwell/components';
import type { GetDashboardInitialData } from 'lib/queries/dashboard';

import { DASHBOARD_FILTERS } from 'lib/queries/dashboard';
import { getLargestRemainderPercentages } from 'lib/utils/getLargestRemainderPercentages';
import { useCannabinoidsStudiesQuery } from 'graphql/research/types-and-hooks';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useDasboardContext } from 'contexts/dashboard-context';

import Loader from './loader';
import CardEmpty from './card-empty';
import LineCounter from './line-counter';

type CannabinoidsList = GetDashboardInitialData['cannabinoids'];

type CannabinoidsGroupedList = {
  category: string;
  cannabinoids: string[];
  studies: number[];
}[];

const mapData = (data: CannabinoidsList): CannabinoidsGroupedList =>
  data.reduce((list, { category, name, studies }) => {
    if (!studies?.length) {
      return list;
    }

    const group = list.find(el => el.category === category);
    const studiesIds = studies.map(({ id }) => id);

    if (group) {
      const uniqueStudies = [...new Set([...group.studies, ...studiesIds])];
      group.cannabinoids.push(name);
      group.studies = uniqueStudies;
    } else {
      list.push({
        category,
        cannabinoids: [name],
        studies: studiesIds,
      });
    }

    return list;
  }, []);

const DashboardCannabinoids: React.FC<{ id: string }> = ({ id }) => {
  const { addToast } = useToast();
  const { translations } = useWordPressContext();
  const {
    filters,
    initialData: { cannabinoids: initialData },
    studies: { byYear, total },
  } = useDasboardContext();
  const [data, updateData] = useReducer(
    (_, newData: CannabinoidsList) => mapData(newData),
    initialData,
    mapData,
  );

  const percentages = useMemo(
    () =>
      getLargestRemainderPercentages(
        data.map(({ studies }) => studies.length),
        2,
      ),
    [data],
  );
  const getPercentage = useCallback(
    key => percentages.find(({ key: itemKey }) => key === itemKey)?.value || 0,
    [percentages],
  );

  const studyIds = useMemo(() => byYear.map(({ id }) => id), [byYear]);
  const variables = useMemo(
    () => ({
      ...(filters?.[DASHBOARD_FILTERS.CANNABINOIDS]?.cannabinoid_id
        ? {
            systemIds: filters[DASHBOARD_FILTERS.CANNABINOIDS].cannabinoid_id,
          }
        : {}),
      ...(filters
        ? { studyIds: { _in: studyIds } as Bigint_Comparison_Exp }
        : {}),
    }),
    [filters?.[DASHBOARD_FILTERS.CANNABINOIDS], studyIds],
  );

  const { loading } = useCannabinoidsStudiesQuery({
    skip: !filters,
    variables,
    onCompleted: data => {
      updateData(data.nodes);
    },
    onError: error => {
      addToast({
        content: error.message,
        key: 'dashboard-studies-year-error',
        type: 'negative',
        shouldRemove: true,
      });
    },
  });

  useMemo(() => {
    if (!filters) {
      updateData(initialData);
    }
  }, [filters]);

  return (
    <section
      {...{
        id,
        className: [
          'grid gap-y-10 md:gap-y-6 gap-x-16',
          data.length > 1 ? 'sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5' : '',
        ].join(' '),
      }}
    >
      <Paragraph
        {...{
          className: 'col-span-full',
          size: 'large',
          tagName: 'header',
        }}
      >
        {translations['Studies by Cannabinoids']}
      </Paragraph>
      {loading ? (
        <Loader fullHeight={false} />
      ) : data.length ? (
        data.map(({ cannabinoids, category, studies }, key) => (
          <section
            {...{
              key,
              className: 'grid gap-y-3 content-start',
            }}
          >
            <Paragraph tagName="header">{category}</Paragraph>
            {total > 1 && (
              <section className="grid gap-y-1">
                <span>{getPercentage(key)}%</span>
                <LineCounter
                  {...{
                    color: 'lilac-300',
                    count: studies.length,
                    total,
                  }}
                />
                <Paragraph size="small">
                  {translations[
                    'A total of %d studies were made for these cannabinoids.'
                  ].replace('%d', studies.length.toString())}
                </Paragraph>
              </section>
            )}
            <ul>
              {cannabinoids.map(name => (
                <Paragraph
                  {...{
                    key: name,
                    size: 'small',
                    variant: 'medium',
                  }}
                >
                  {name}
                </Paragraph>
              ))}
            </ul>
          </section>
        ))
      ) : (
        <CardEmpty />
      )}
    </section>
  );
};

export default DashboardCannabinoids;
